import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Phone, Mail, MapPin, Calendar, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { NotifySettings } from "@/components/admin/NotifySettings";
import { TeamSettings } from "@/components/admin/TeamSettings";
import { Analytics } from "@/components/admin/Analytics";
import { http, errMsg } from "@/lib/api";

const STATUSES = [["new", "New"], ["in_review", "In review"], ["quoted", "Quoted"], ["scheduled", "Scheduled"], ["completed", "Completed"]];

const AuthImage = ({ path, onOpen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null;
    let alive = true;
    http.get(`/admin/files/${path}`, { responseType: "blob" })
      .then((r) => { if (alive) { u = URL.createObjectURL(r.data); setUrl(u); } })
      .catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [path]);
  return (
    <button type="button" onClick={() => url && onOpen(url)} className="aspect-square bg-[var(--steel)] overflow-hidden border border-[var(--line)] hover:border-[var(--orange)] transition-colors" data-testid="admin-photo-thumb">
      {url ? <img src={url} alt="Customer upload" className="w-full h-full object-cover" /> : <span className="block w-full h-full animate-pulse" />}
    </button>
  );
};

const QuoteCard = ({ q, onStatus, onOpen }) => (
  <article className="border border-[var(--line)] bg-[var(--char)] p-6" data-testid={`admin-quote-${q.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="font-mono-x text-xs text-zinc-400">{new Date(q.created_at).toLocaleString()}</p>
        <h3 className="font-display font-black uppercase text-2xl mt-1">{q.name}</h3>
        <p className="text-[var(--orange)] font-display font-bold uppercase">{q.service || "General request"}</p>
      </div>
      <select value={q.status} onChange={(e) => onStatus(q.id, e.target.value)} className="field !w-auto !py-2 pr-10" data-testid={`admin-status-${q.id}`}>
        {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
      <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-white"><Phone className="w-4 h-4 text-[var(--orange)]" />{q.phone}</a>
      <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-white break-all"><Mail className="w-4 h-4 text-[var(--orange)]" />{q.email}</a>
      {q.town && <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--orange)]" />{q.town}</span>}
      {q.preferred_date && <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--orange)]" />{q.preferred_date}</span>}
      {q.contact_method && <span className="font-mono-x text-xs uppercase border border-[var(--line)] px-2 py-1">Prefers {q.contact_method}</span>}
    </div>
    <p className="mt-4 text-zinc-200 whitespace-pre-wrap leading-relaxed">{q.details}</p>
    {Array.isArray(q.email_log) && q.email_log.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-2" data-testid={`admin-email-log-${q.id}`}>
        {q.email_log.map((l, i) => (
          <span key={i} title={l.info} className={`font-mono-x text-[11px] px-2 py-1 border ${l.ok ? "border-emerald-600 text-emerald-400" : "border-red-600 text-red-400"}`}>
            {l.ok ? "Emailed" : "Email failed"}{l.to ? ` · ${l.to}` : ""}{l.ok ? "" : ` · ${l.info}`}
          </span>
        ))}
      </div>
    )}
    {q.photos?.length > 0 && (
      <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-2">
        {q.photos.map((p) => <AuthImage key={p.path} path={p.path} onOpen={onOpen} />)}
      </div>
    )}
  </article>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [tab, setTab] = useState("quotes");

  const load = useCallback(async () => {
    try {
      const r = await http.get("/admin/quotes");
      setQuotes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin"); else toast.error(errMsg(e));
    }
  }, [nav]);

  useEffect(() => { document.title = "Quote requests | Assemble Pro"; load(); }, [load]);

  const onStatus = async (id, status) => {
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    try { await http.patch(`/admin/quotes/${id}`, { status }); toast.success("Status updated"); } catch (e) { toast.error(errMsg(e)); load(); }
  };

  const logout = async () => { await http.post("/auth/logout").catch(() => {}); nav("/admin"); };
  const list = (quotes || []).filter((q) => filter === "all" || q.status === filter);

  return (
    <div className="min-h-screen bg-[var(--ink)]" data-testid="admin-dashboard">
      <header className="border-b border-[var(--line)] bg-[var(--char)]">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <Logo testId="admin-logo" />
          <div className="flex gap-2">
            <button onClick={load} className="btn-ghost px-3 py-2" aria-label="Refresh" data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={logout} className="btn-ghost px-4 py-2" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-5 py-10">
        <h1 className="font-display font-black uppercase text-5xl">Dashboard</h1>
        <div className="mt-6 flex border-b border-[var(--line)]" data-testid="admin-tabs">
          {[["quotes", "Quotes"], ["analytics", "Analytics"], ["settings", "Settings"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
              className={`px-5 py-3 font-display text-lg font-bold uppercase border-b-2 -mb-px transition-colors ${tab === v ? "border-[var(--orange)] text-[var(--orange)]" : "border-transparent text-zinc-300 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {tab === "analytics" && <Analytics />}
        {tab === "settings" && <><NotifySettings /><TeamSettings /></>}
        {tab === "quotes" && <>
        <div className="mt-6 flex gap-2 overflow-x-auto pb-2" data-testid="admin-filters">
          {[["all", "All"], ...STATUSES].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`admin-filter-${v}`}
              className={`shrink-0 px-4 py-2 font-mono-x text-xs uppercase tracking-wider border transition-colors ${filter === v ? "bg-[var(--orange)] text-[var(--ink)] border-[var(--orange)]" : "border-[var(--line)] text-zinc-300 hover:border-zinc-500"}`}>
              {l} {v === "all" ? `(${quotes?.length ?? 0})` : `(${(quotes || []).filter((q) => q.status === v).length})`}
            </button>
          ))}
        </div>
        <div className="mt-8 space-y-4">
          {quotes === null && <p className="text-zinc-400">Loading…</p>}
          {quotes && list.length === 0 && <p className="text-zinc-400" data-testid="admin-empty">No quote requests here yet.</p>}
          {list.map((q) => <QuoteCard key={q.id} q={q} onStatus={onStatus} onOpen={setLightbox} />)}
        </div>
        </>}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-5" onClick={() => setLightbox(null)} data-testid="admin-lightbox">
          <button className="absolute top-5 right-5 w-11 h-11 grid place-items-center bg-[var(--orange)] text-[var(--ink)]" aria-label="Close"><X className="w-5 h-5" /></button>
          <img src={lightbox} alt="Customer upload full size" className="max-h-[90vh] max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}
